import React, { useEffect, useState } from "react";
import {
    createDispatchCenter,
    getDispatchCenters,
    assignDispatch,
    getDispatchAssignments,
    completeDispatchAssignment,
} from "../../api/dispatchCenterService";

export default function DispatchCenterPage() {
    const [centers, setCenters] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [selectedCenterId, setSelectedCenterId] = useState("");

    const [form, setForm] = useState({
        name: "",
        addressId: "",
        managerId: "",
    });

    const [assignment, setAssignment] = useState({
        shipmentId: "",
        driverId: "",
        notes: "",
    });

    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const loadCenters = async () => {
        try {
            setError("");
            const data = await getDispatchCenters();
            setCenters(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(err?.message || "Failed to load dispatch centers.");
        }
    };

    useEffect(() => {
        loadCenters();
    }, []);

    const handleCreate = async () => {
        try {
            setError("");
            setSuccess("");

            await createDispatchCenter({
                name: form.name,
                addressId: form.addressId ? Number(form.addressId) : null,
                managerId: form.managerId ? Number(form.managerId) : null,
            });

            setSuccess("Dispatch center created.");
            setForm({ name: "", addressId: "", managerId: "" });
            await loadCenters();
        } catch (err) {
            setError(err?.message || "Failed to create dispatch center.");
        }
    };

    const handleAssign = async () => {
        try {
            setError("");
            setSuccess("");

            await assignDispatch(selectedCenterId, {
                shipmentId: Number(assignment.shipmentId),
                driverId: assignment.driverId ? Number(assignment.driverId) : null,
                notes: assignment.notes,
            });

            setSuccess("Dispatch assignment created.");
            setAssignment({ shipmentId: "", driverId: "", notes: "" });
            await loadAssignments();
        } catch (err) {
            setError(err?.message || "Failed to create dispatch assignment.");
        }
    };

    const loadAssignments = async () => {
        try {
            setError("");

            if (!selectedCenterId) {
                setError("Dispatch Center ID is required.");
                return;
            }

            const data = await getDispatchAssignments(selectedCenterId);
            setAssignments(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(err?.message || "Failed to load assignments.");
        }
    };

    const handleComplete = async (assignmentId) => {
        try {
            setError("");
            setSuccess("");

            await completeDispatchAssignment(assignmentId);
            setSuccess("Assignment completed.");
            await loadAssignments();
        } catch (err) {
            setError(err?.message || "Failed to complete assignment.");
        }
    };

    return (
        <div className="max-w-7xl mx-auto p-6 bg-white rounded shadow">
            <h2 className="text-2xl font-bold mb-4">
                Dispatch Center Management
            </h2>

            {error && <p className="text-red-600 mb-3">{error}</p>}
            {success && <p className="text-green-600 mb-3">{success}</p>}

            <div className="border rounded p-4 mb-6">
                <h3 className="text-lg font-bold mb-3">
                    Create Dispatch Center
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input
                        value={form.name}
                        onChange={(e) =>
                            setForm((p) => ({ ...p, name: e.target.value }))
                        }
                        placeholder="Center name"
                        className="border p-2 rounded"
                    />

                    <input
                        type="number"
                        value={form.addressId}
                        onChange={(e) =>
                            setForm((p) => ({ ...p, addressId: e.target.value }))
                        }
                        placeholder="Address ID"
                        className="border p-2 rounded"
                    />

                    <input
                        type="number"
                        value={form.managerId}
                        onChange={(e) =>
                            setForm((p) => ({ ...p, managerId: e.target.value }))
                        }
                        placeholder="Manager User ID"
                        className="border p-2 rounded"
                    />
                </div>

                <button
                    onClick={handleCreate}
                    className="mt-3 bg-green-700 text-white px-4 py-2 rounded"
                >
                    Create Center
                </button>
            </div>

            <div className="border rounded p-4 mb-6">
                <h3 className="text-lg font-bold mb-3">
                    Dispatch Assignment
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <input
                        type="number"
                        value={selectedCenterId}
                        onChange={(e) => setSelectedCenterId(e.target.value)}
                        placeholder="Dispatch Center ID"
                        className="border p-2 rounded"
                    />

                    <input
                        type="number"
                        value={assignment.shipmentId}
                        onChange={(e) =>
                            setAssignment((p) => ({
                                ...p,
                                shipmentId: e.target.value,
                            }))
                        }
                        placeholder="Shipment ID"
                        className="border p-2 rounded"
                    />

                    <input
                        type="number"
                        value={assignment.driverId}
                        onChange={(e) =>
                            setAssignment((p) => ({
                                ...p,
                                driverId: e.target.value,
                            }))
                        }
                        placeholder="Driver ID"
                        className="border p-2 rounded"
                    />

                    <input
                        value={assignment.notes}
                        onChange={(e) =>
                            setAssignment((p) => ({ ...p, notes: e.target.value }))
                        }
                        placeholder="Notes"
                        className="border p-2 rounded"
                    />
                </div>

                <div className="flex gap-3 mt-3">
                    <button
                        onClick={handleAssign}
                        className="bg-blue-700 text-white px-4 py-2 rounded"
                    >
                        Assign
                    </button>

                    <button
                        onClick={loadAssignments}
                        className="bg-purple-700 text-white px-4 py-2 rounded"
                    >
                        Load Assignments
                    </button>
                </div>
            </div>

            <div className="border rounded p-4 mb-6">
                <h3 className="text-lg font-bold mb-3">Dispatch Centers</h3>

                <table className="w-full border text-sm">
                    <thead>
                        <tr className="bg-gray-100">
                            <th className="border p-2">ID</th>
                            <th className="border p-2">Name</th>
                            <th className="border p-2">Address</th>
                            <th className="border p-2">Manager</th>
                            <th className="border p-2">Active</th>
                        </tr>
                    </thead>

                    <tbody>
                        {centers.map((item) => (
                            <tr key={item.id}>
                                <td className="border p-2">{item.id}</td>
                                <td className="border p-2">{item.name}</td>
                                <td className="border p-2">{item.addressId || "�"}</td>
                                <td className="border p-2">{item.managerId || "�"}</td>
                                <td className="border p-2">
                                    {item.isActive ? "Yes" : "No"}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {assignments.length > 0 && (
                <div className="border rounded p-4">
                    <h3 className="text-lg font-bold mb-3">
                        Dispatch Assignments
                    </h3>

                    <table className="w-full border text-sm">
                        <thead>
                            <tr className="bg-gray-100">
                                <th className="border p-2">ID</th>
                                <th className="border p-2">Shipment</th>
                                <th className="border p-2">Driver</th>
                                <th className="border p-2">Status</th>
                                <th className="border p-2">Notes</th>
                                <th className="border p-2">Assigned At</th>
                                <th className="border p-2">Completed At</th>
                                <th className="border p-2">Action</th>
                            </tr>
                        </thead>

                        <tbody>
                            {assignments.map((item) => (
                                <tr key={item.id}>
                                    <td className="border p-2">{item.id}</td>
                                    <td className="border p-2">{item.shipmentId}</td>
                                    <td className="border p-2">{item.driverId || "�"}</td>
                                    <td className="border p-2">{item.status}</td>
                                    <td className="border p-2">{item.notes || "�"}</td>
                                    <td className="border p-2">
                                        {item.assignedAt
                                            ? new Date(item.assignedAt).toLocaleString()
                                            : "�"}
                                    </td>
                                    <td className="border p-2">
                                        {item.completedAt
                                            ? new Date(item.completedAt).toLocaleString()
                                            : "�"}
                                    </td>
                                    <td className="border p-2">
                                        {item.status !== "Completed" && (
                                            <button
                                                onClick={() => handleComplete(item.id)}
                                                className="bg-green-700 text-white px-3 py-1 rounded"
                                            >
                                                Complete
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}